/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Breadcrumbs } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { IndexTable } from "../../../components/index_table";
import { Loader } from "../../../components/loader";
import { MenuHeading } from "../../../components/menu_heading";
import { ResetButton } from "../../../components/reset_button";
import { UpdateButton } from "../../../components/update_button";
import { OrderNumberCell } from "../../../components/order_number_cell";
import {
  RestaurantScopeFilters,
  defaultRestaurantScope,
  scopeToParams,
} from "../../../components/restaurant_scope_filters";
import { Query_Keys } from "../../../lib/queryKeys";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { fetchDailyLogbookIndex } from "./api";
import { DailyLogbookSummary } from "./summary";
import { DailyLogbookDetailsModal } from "./details_modal";
import { DailyLogbookExportDialog } from "./export_dialog";
import { buildGroupExportUrl, exportFilename } from "./export";
import { useDailyLogbookExport } from "./use_daily_logbook_export";
import { EMPTY_DISPLAY, renderMetricCell, renderVarianceCell } from "./cells";
import {
  StyledBreadCrumbContainer,
  StyledLink,
  filterContainerStyles,
  datePickerStyles,
  comparisonLegendStyles,
  comparisonLegendSeparatorStyles,
} from "./styles";

const INVALID_DATE_MESSAGE = "Business date must be a valid ISO date (yyyy-mm-dd)";

const VARIANCE_METRIC = "net_sales";

const defaultLogbookFilters = () => ({
  ...defaultRestaurantScope(),
  business_date: dayjs(),
});

const normalizeDayjsValue = (value) => {
  if (!value) return null;
  if (typeof value?.isValid === "function" && typeof value?.format === "function") {
    return value.isValid() ? value : null;
  }
  const parsed = dayjs(value?.$d || value);
  return parsed.isValid() ? parsed : null;
};

// The API is ISO-only on input even though the report prints mm/dd/yyyy.
const toIsoDate = (value) => {
  const parsed = normalizeDayjsValue(value);
  return parsed ? parsed.format("YYYY-MM-DD") : undefined;
};

export const DailyLogbook = () => {
  const showNotification = useNotificationContext();

  const [filters, setFilters] = useState(defaultLogbookFilters());
  const [appliedFilters, setAppliedFilters] = useState(defaultLogbookFilters());
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [scopeResetToken, setScopeResetToken] = useState(0);

  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  const { exporting, runExport } = useDailyLogbookExport();

  const appliedBusinessDate = toIsoDate(appliedFilters.business_date);

  const fetchLogbook = async () => {
    const response = await fetchDailyLogbookIndex({
      page,
      per_page: itemsPerPage,
      business_date: appliedBusinessDate,
      ...scopeToParams(appliedFilters),
    });

    return {
      ...(response?.data || {}),
      pageCount: Number(response?.headers?.["total-count"] || 0),
    };
  };

  const {
    data: logbook,
    isFetching,
    error,
  } = useQuery({
    queryKey: Query_Keys.daily_logbooks({
      queryFilter: { ...appliedFilters, business_date: appliedBusinessDate },
      page,
      itemsPerPage,
    }),
    queryFn: fetchLogbook,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!error) return;
    showNotification(error?.response?.data?.error || "Failed to load daily logbook.", "error");
  }, [error, showNotification]);

  const handleScopeChange = useCallback((nextScope) => {
    setFilters((prev) => ({ ...prev, ...nextScope }));
    setFiltersChanged(true);
  }, []);

  const handleBusinessDateChange = (value) => {
    setFilters((prev) => ({ ...prev, business_date: normalizeDayjsValue(value) }));
    setFiltersChanged(true);
  };

  const handleApplyFilters = () => {
    if (!filters.business_date) {
      showNotification(INVALID_DATE_MESSAGE, "error");
      return;
    }
    setAppliedFilters(filters);
    setFiltersChanged(false);
    setPage(1);
  };

  const handleReset = () => {
    const resetFilters = defaultLogbookFilters();
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setFiltersChanged(false);
    setPage(1);
    setItemsPerPage(25);
    setScopeResetToken((token) => token + 1);
  };

  const handleViewDetails = useCallback((restaurant) => setSelectedRestaurant(restaurant), []);
  const handleCloseDetails = useCallback(() => setSelectedRestaurant(null), []);

  const handleOpenExport = useCallback(() => setExportOpen(true), []);
  const handleCloseExport = useCallback(() => setExportOpen(false), []);

  const handleExport = useCallback(
    async (format) => {
      const downloaded = await runExport({
        format,
        url: buildGroupExportUrl({
          format,
          businessDate: appliedBusinessDate,
          scope: scopeToParams(appliedFilters),
        }),
        fallbackFilename: exportFilename({ format, businessDate: appliedBusinessDate }),
      });

      if (downloaded) setExportOpen(false);
    },
    [runExport, appliedBusinessDate, appliedFilters],
  );

  const responseColumns = logbook?.columns;

  const metricDefinitions = useMemo(() => {
    const source = logbook?.group?.metrics?.length
      ? logbook.group.metrics
      : logbook?.restaurants?.[0]?.metrics;

    return (source || []).map(({ metric, label, kind }) => ({ metric, label, kind }));
  }, [logbook]);

  const columns = useMemo(
    () => [
      {
        key: "restaurant",
        label: "Restaurant",
        sortable: false,
        width: 220,
      },
      ...metricDefinitions.map((definition) => ({
        key: `metric_${definition.metric}`,
        label: definition.label,
        sortable: false,
        align: "center",
        width: 130,
      })),
      {
        key: "var_sdlw",
        label: "Net vs SDLW",
        sortable: false,
        align: "center",
        width: 130,
      },
      {
        key: "var_sdly",
        label: "Net vs SDLY",
        sortable: false,
        align: "center",
        width: 130,
      },
      {
        key: "action",
        label: "Action",
        sortable: false,
        align: "center",
        width: 90,
      },
    ],
    [metricDefinitions],
  );

  const rows = useMemo(
    () =>
      (logbook?.restaurants || []).map((restaurant) => {
        const cellsByMetric = new Map(
          (restaurant.metrics || []).map((metric) => [metric.metric, metric]),
        );
        const varianceRow = cellsByMetric.get(VARIANCE_METRIC);

        const metricCells = metricDefinitions.reduce((accumulator, definition) => {
          accumulator[`metric_${definition.metric}`] = renderMetricCell(
            cellsByMetric.get(definition.metric)?.today,
          );
          return accumulator;
        }, {});

        return {
          id: restaurant.restaurant_id,
          restaurantId: restaurant.restaurant_id,
          restaurant: restaurant.name,
          ...metricCells,
          var_sdlw: renderVarianceCell(varianceRow?.var_sdlw),
          var_sdly: renderVarianceCell(varianceRow?.var_sdly),
          action: (
            <OrderNumberCell
              orderValue={restaurant.restaurant_id}
              onView={() => handleViewDetails(restaurant)}
            />
          ),
        };
      }),
    [logbook?.restaurants, metricDefinitions, handleViewDetails],
  );

  const getRowProps = (row) => ({
    "data-restaurant-id": row.restaurantId,
  });

  const comparisonLegend = responseColumns ? (
    <Box sx={comparisonLegendStyles} data-testid="daily-logbook-comparison-legend">
      <span>{`Today: ${responseColumns.today ?? EMPTY_DISPLAY}`}</span>
      <Box component="span" aria-hidden="true" sx={comparisonLegendSeparatorStyles}>
        •
      </Box>
      <span>{`SDLW: ${responseColumns.sdlw ?? EMPTY_DISPLAY}`}</span>
      <Box component="span" aria-hidden="true" sx={comparisonLegendSeparatorStyles}>
        •
      </Box>
      <span>{`SDLY: ${responseColumns.sdly ?? EMPTY_DISPLAY}`}</span>
    </Box>
  ) : null;

  return (
    <Box sx={{ height: "100%", width: "100%", overflowY: "scroll", overflowX: "hidden" }}>
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
          <StyledLink color="#515152">Reports</StyledLink>
          <StyledLink color="#515152">Sales</StyledLink>
          <StyledLink color="#515152">Daily Logbook</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <Box sx={filterContainerStyles}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", lg: "center" },
            gap: { xs: 2, lg: 1 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: "12px" },
              flexWrap: "wrap",
              alignItems: { xs: "flex-start", sm: "center" },
              flex: 1,
            }}
          >
            <RestaurantScopeFilters
              key={scopeResetToken}
              source="daily_logbook"
              testIdPrefix="daily-logbook"
              value={filters}
              onChange={handleScopeChange}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="MM/DD/YYYY"
                value={normalizeDayjsValue(filters.business_date)}
                onChange={handleBusinessDateChange}
                slots={{ openPickerIcon: CalendarMonthIcon }}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: { ...datePickerStyles, minWidth: 160 },
                    inputProps: { "data-testid": "daily-logbook-business-date" },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              width: { xs: "100%", lg: "auto" },
            }}
          >
            <ResetButton onClick={handleReset} />
            <UpdateButton
              onClick={handleApplyFilters}
              filtersChanged={filtersChanged}
              status={isFetching}
            />
          </Box>
        </Box>
      </Box>

      <MenuHeading report={false}>Daily Logbook</MenuHeading>

      {comparisonLegend ? <Box sx={{ px: 2 }}>{comparisonLegend}</Box> : null}

      <Box sx={{ px: 2 }}>
        <DailyLogbookSummary metrics={logbook?.group?.metrics || []} loading={isFetching} />
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        {isFetching ? (
          <Box data-testid="daily-logbook-table-loader">
            <Loader />
          </Box>
        ) : (
          // csvLoading is left off deliberately: it swaps the download control for an
          // unlabelled spinner mid-export and drops keyboard focus. The picker's own
          // Download button already carries the progress state.
          <IndexTable
            report={false}
            columns={columns}
            data={rows}
            type="restaurants"
            tableId="daily-logbook"
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={logbook?.pageCount || 0}
            getRowProps={getRowProps}
            emptyStateText="No logbook data for the selected filters"
            showDownloadButton
            downloadButtonLabel="Export daily logbook"
            disableDownloadButton={rows.length === 0 || exporting}
            handleExportCsv={handleOpenExport}
          />
        )}
      </Box>

      <DailyLogbookExportDialog
        open={exportOpen}
        onClose={handleCloseExport}
        onConfirm={handleExport}
        loading={exporting}
        testId="daily-logbook-export-dialog"
        description={`Select the format you want to download. This report covers every restaurant in the current filters${
          responseColumns?.today ? ` for ${responseColumns.today}` : ""
        }.`}
      />

      <DailyLogbookDetailsModal
        open={Boolean(selectedRestaurant)}
        restaurant={selectedRestaurant}
        businessDate={appliedBusinessDate}
        businessDateLabel={responseColumns?.today}
        onClose={handleCloseDetails}
      />
    </Box>
  );
};
